import React, {useState, useMemo, useRef} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {useNavigate} from "react-router";
import {usePublishedInstructors} from "../firebase/usePublishedInstructors";
import {InstructorsMap} from "../instructors/InstructorsMap";
import {InstructorCard} from "../instructors/InstructorCard";
import {InstructorFilterBar} from "../instructors/InstructorFilterBar";
import {extractCountiesWithCount, filterInstructors} from "../instructors/filterInstructors";
import {hasActiveUpcomingCourses} from "../instructors/courseValidation";
import {CourseInterestModal} from "../instructors/CourseInterestModal";

export const InstructorsSection = () => {
    const navigate = useNavigate();

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCounty, setSelectedCounty] = useState("Alle");
    const [onlyWithUpcomingCourses, setOnlyWithUpcomingCourses] = useState(false);
    const [selectedInstructorId, setSelectedInstructorId] = useState(null);
    const [targetCoordinates, setTargetCoordinates] = useState(null);
    const [interestModalInstructor, setInterestModalInstructor] = useState(null);
    const [interestModalCourse, setInterestModalCourse] = useState(null);
    const carouselRef = useRef(null);

    const rawInstructors = usePublishedInstructors({});

    const counties = useMemo(() => {
        return extractCountiesWithCount(rawInstructors);
    }, [rawInstructors]);

    const upcomingCoursesCount = useMemo(() => {
        return (rawInstructors || []).filter(hasActiveUpcomingCourses).length;
    }, [rawInstructors]);

    const filteredInstructors = useMemo(() => {
        return filterInstructors(rawInstructors, {
            searchTerm,
            selectedCounty,
            onlyWithUpcomingCourses,
        });
    }, [rawInstructors, searchTerm, selectedCounty, onlyWithUpcomingCourses]);

    const handleLocate = (instructor) => {
        if (instructor && typeof instructor.lat === "number" && typeof instructor.lon === "number") {
            setSelectedInstructorId(instructor.id);
            setTargetCoordinates([instructor.lat, instructor.lon]);

            const mapElement = document.getElementById("landing-instructors-map");
            if (mapElement && typeof mapElement.scrollIntoView === "function") {
                const prefersReducedMotion =
                    typeof window !== "undefined" &&
                    window.matchMedia &&
                    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

                mapElement.scrollIntoView({
                    behavior: prefersReducedMotion ? "auto" : "smooth",
                    block: "center",
                });
            }
        }
    };

    const handleResetFilters = () => {
        setSearchTerm("");
        setSelectedCounty("Alle");
        setOnlyWithUpcomingCourses(false);
    };

    const handleExpressInterest = (instructor, course) => {
        setInterestModalInstructor(instructor);
        setInterestModalCourse(course || null);
    };

    const handleScrollLeft = () => {
        if (carouselRef.current) {
            if (typeof carouselRef.current.scrollBy === "function") {
                carouselRef.current.scrollBy({left: -360, behavior: "smooth"});
            } else {
                carouselRef.current.scrollLeft = (carouselRef.current.scrollLeft || 0) - 360;
            }
        }
    };

    const handleScrollRight = () => {
        if (carouselRef.current) {
            if (typeof carouselRef.current.scrollBy === "function") {
                carouselRef.current.scrollBy({left: 360, behavior: "smooth"});
            } else {
                carouselRef.current.scrollLeft = (carouselRef.current.scrollLeft || 0) + 360;
            }
        }
    };

    return (
        <Box
            component="section"
            id="finn-kursholder"
            aria-labelledby="instructors-title"
            sx={{
                py: {xs: 4, md: 6},
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                overflowX: "clip",
            }}
        >
            {/* Section Header */}
            <Box sx={{mb: 3}}>
                <Typography
                    id="instructors-title"
                    variant="h4"
                    component="h2"
                    sx={{
                        fontWeight: 800,
                        fontSize: {xs: "1.6rem", sm: "2rem", md: "2.25rem"},
                        color: "primary.main",
                        mb: 1.5,
                        letterSpacing: "-0.01em",
                    }}
                >
                    Finn sertifisert kursholder
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{maxWidth: 800, fontSize: {xs: "0.975rem", sm: "1.05rem"}, lineHeight: 1.7}}
                >
                    Her finner du en oversikt over sertifiserte kursholdere i bekymringsmestring som har
                    registrert seg i vårt register. Dersom du ikke finner noen i din umiddelbare nærhet, kan det
                    likevel lønne seg å kontakte din hjemkommune eller fastlege for å høre om kurset tilbys lokalt.
                </Typography>
            </Box>

            {/* Filter Bar with Wrapping Chips and Dropdown */}
            <InstructorFilterBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                counties={counties}
                selectedCounty={selectedCounty}
                onSelectCounty={setSelectedCounty}
                onlyWithUpcomingCourses={onlyWithUpcomingCourses}
                onToggleUpcomingCourses={() => setOnlyWithUpcomingCourses((prev) => !prev)}
                upcomingCoursesCount={upcomingCoursesCount}
                resultCount={filteredInstructors.length}
            />

            {/* Generous Full-Width Centerpiece Map */}
            <Box
                id="landing-instructors-map"
                sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    height: {xs: 340, sm: 400, md: 460},
                    borderRadius: 3,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "rgba(40, 82, 56, 0.12)",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
                    mb: 4,
                }}
            >
                <InstructorsMap
                    instructors={filteredInstructors}
                    targetCoordinates={targetCoordinates}
                    height="100%"
                    minHeight={340}
                    scrollWheelZoom={false}
                    onExpressInterest={handleExpressInterest}
                />
            </Box>

            {/* Curated Spotlight Instructors Preview */}
            <Box sx={{width: "100%", maxWidth: "100%", minWidth: 0, mb: 3}}>
                <Box
                    sx={{
                        mb: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1.5,
                    }}
                >
                    <Box sx={{display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 1}}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{fontWeight: 600}}>
                            {filteredInstructors.length > 0
                                ? `Viser ${filteredInstructors.length} ${
                                      filteredInstructors.length === 1 ? "kursholder" : "kursholdere"
                                  }${selectedCounty !== "Alle" ? ` i ${selectedCounty}` : ""}`
                                : "Ingen kursholdere funnet"}
                        </Typography>
                        {filteredInstructors.length > 1 && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{fontStyle: "italic"}}
                            >
                                (Bruk pilene eller sveip for å bla)
                            </Typography>
                        )}
                    </Box>
                </Box>

                {filteredInstructors.length > 0 ? (
                    <Box sx={{position: "relative", width: "100%", maxWidth: "100%", minWidth: 0}}>
                        {/* Floating Left Arrow Overlay */}
                        {filteredInstructors.length > 1 && (
                            <Tooltip title="Forrige kursholder">
                                <IconButton
                                    size="medium"
                                    onClick={handleScrollLeft}
                                    aria-label="Forrige kursholder"
                                    sx={{
                                        position: "absolute",
                                        left: {xs: 6, sm: 12, md: 14},
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 4,
                                        width: 44,
                                        height: 44,
                                        borderRadius: "50%",
                                        border: "1px solid rgba(40, 82, 56, 0.22)",
                                        bgcolor: "rgba(255, 255, 255, 0.95)",
                                        backdropFilter: "blur(6px)",
                                        color: "primary.main",
                                        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.16)",
                                        "&:hover": {
                                            bgcolor: "#FFFFFF",
                                            boxShadow: "0 6px 18px rgba(0, 0, 0, 0.22)",
                                            transform: "translateY(-50%) scale(1.05)",
                                        },
                                        "&:focus-visible": {
                                            outline: "2px solid #285238",
                                            outlineOffset: 2,
                                        },
                                    }}
                                >
                                    <ChevronLeftIcon fontSize="medium" />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Floating Right Arrow Overlay */}
                        {filteredInstructors.length > 1 && (
                            <Tooltip title="Neste kursholder">
                                <IconButton
                                    size="medium"
                                    onClick={handleScrollRight}
                                    aria-label="Neste kursholder"
                                    sx={{
                                        position: "absolute",
                                        right: {xs: 6, sm: 12, md: 14},
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 4,
                                        width: 44,
                                        height: 44,
                                        borderRadius: "50%",
                                        border: "1px solid rgba(40, 82, 56, 0.22)",
                                        bgcolor: "rgba(255, 255, 255, 0.95)",
                                        backdropFilter: "blur(6px)",
                                        color: "primary.main",
                                        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.16)",
                                        "&:hover": {
                                            bgcolor: "#FFFFFF",
                                            boxShadow: "0 6px 18px rgba(0, 0, 0, 0.22)",
                                            transform: "translateY(-50%) scale(1.05)",
                                        },
                                        "&:focus-visible": {
                                            outline: "2px solid #285238",
                                            outlineOffset: 2,
                                        },
                                    }}
                                >
                                    <ChevronRightIcon fontSize="medium" />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Box
                            ref={carouselRef}
                            sx={{
                                display: "flex",
                                overflowX: "auto",
                                scrollSnapType: "x mandatory",
                                scrollBehavior: "smooth",
                                WebkitOverflowScrolling: "touch",
                                gap: 2.5,
                                py: 1,
                                px: {xs: 1, md: 0},
                                width: "100%",
                                maxWidth: "100%",
                                minWidth: 0,
                                "&::-webkit-scrollbar": {display: "none"},
                                msOverflowStyle: "none",
                                scrollbarWidth: "none",
                            }}
                        >
                            {filteredInstructors.map((instructor) => (
                                <Box
                                    key={instructor.id}
                                    sx={{
                                        // Peek effect: 82% on mobile reveals next card peeking from right edge!
                                        flex: {xs: "0 0 82%", sm: "0 0 330px", md: "0 0 350px"},
                                        minWidth: 0,
                                        scrollSnapAlign: "start",
                                        display: "flex",
                                    }}
                                >
                                    <InstructorCard
                                        instructor={instructor}
                                        onLocate={handleLocate}
                                        isSelected={selectedInstructorId === instructor.id}
                                        onExpressInterest={handleExpressInterest}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            textAlign: "center",
                            py: 5,
                            px: 3,
                            bgcolor: "background.paper",
                            borderRadius: 3,
                            border: "1px dashed",
                            borderColor: "divider",
                        }}
                    >
                        <Typography variant="h6" color="text.secondary" sx={{mb: 1, fontWeight: 600}}>
                            Ingen kursholdere matchet søket
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 2.5}}>
                            Prøv å endre søkeord eller velg et annet fylke for å se tilgjengelige kursholdere.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleResetFilters}
                            sx={{
                                borderRadius: 2,
                                fontWeight: 600,
                                minHeight: 44,
                                "&:focus-visible": {
                                    outline: "2px solid #285238",
                                    outlineOffset: "2px",
                                },
                            }}
                        >
                            Nullstill filtre
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Full Directory CTA Banner */}
            <Box
                sx={{
                    p: {xs: 2, sm: 3.5},
                    borderRadius: 3,
                    bgcolor: "rgba(40, 82, 56, 0.05)",
                    border: "1px solid",
                    borderColor: "rgba(40, 82, 56, 0.12)",
                    display: "flex",
                    flexDirection: {xs: "column", sm: "row"},
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    textAlign: {xs: "center", sm: "left"},
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                }}
            >
                <Box sx={{minWidth: 0, width: "100%"}}>
                    <Typography variant="subtitle1" sx={{fontWeight: 700, color: "primary.main", mb: 0.5}}>
                        Ønsker du å søke i hele kursholderregisteret?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        I den komplette katalogen kan du bla gjennom samtlige kursholdere med detaljert filtrering og interaktivt kart.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate("/find-instructor")}
                    sx={{
                        fontWeight: 700,
                        px: {xs: 2, sm: 3},
                        py: 1.25,
                        borderRadius: 2.5,
                        whiteSpace: {xs: "normal", sm: "nowrap"},
                        maxWidth: "100%",
                        width: {xs: "100%", sm: "auto"},
                        flexShrink: 0,
                    }}
                >
                    {`Se alle ${filteredInstructors.length} kursholdere`}
                </Button>
            </Box>

            <CourseInterestModal
                open={Boolean(interestModalInstructor)}
                instructor={interestModalInstructor}
                selectedCourse={interestModalCourse}
                onClose={() => {
                    setInterestModalInstructor(null);
                    setInterestModalCourse(null);
                }}
            />
        </Box>
    );
};
